import { FC } from 'react';
import {
  useGetUserCoursesQuery,
  useReviewUserProjectMutation,
  UserProjectStatus,
  type UserProject,
} from '@teachur/http-client';
import { formatSubmittedDate, stripHtml } from '../../utils/projectReviewFormatters';
import { ReviewForm, type ReviewFormSubmitData } from '@new/common/ui/ReviewForm/ReviewForm';
import { ProjectFile } from './ProjectFile';
import { ProjectDrawerCourseDetails } from './ProjectDrawerCourseDetails';
import { ProjectDrawerSection } from './ProjectDrawerSection';
import {
  getInitialReviewState,
  getSortedProjectGoals,
  getLatestSubmission,
} from '../../utils/projectReviewDrawerHelpers';
import { useProjectReviewPermissions } from '../../hooks/useProjectReviewPermissions';

interface ProjectDrawerBodyProps {
  project: UserProject | null;
  onOpenFilePreview?: () => void;
  onOpenCourseDetails?: (courseId: number) => void;
}

export const ProjectDrawerBody: FC<ProjectDrawerBodyProps> = ({
  project,
  onOpenFilePreview,
  onOpenCourseDetails,
}) => {
  const { mutate: reviewUserProject } = useReviewUserProjectMutation();
  const { data: userCourses = [] } = useGetUserCoursesQuery(project?.userId ?? 0, {
    enabled: !!project?.userId,
  });
  const courseIds = project?.project?.courses?.map((course) => course.id) ?? [];

  const isCourseCompleted = userCourses.some(
    (userCourse) => courseIds.includes(userCourse.courseId) && userCourse.passed
  );

  const { isReviewDisabled } = useProjectReviewPermissions(project, isCourseCompleted);

  const latestSubmission = getLatestSubmission(project);

  const onSubmit = (data: ReviewFormSubmitData) => {
    if (!project || isReviewDisabled || !latestSubmission) return;
    const grades = data.grades.map((g) => ({
      goalId: g.goalId,
      grade: g.rating,
      note: g.note ?? undefined,
    }));
    const status = data.approved ? UserProjectStatus.Approved : UserProjectStatus.NotYet;
    reviewUserProject({
      id: project.id,
      submissionId: latestSubmission.id,
      userProject: { status, note: data.generalNote },
      grades,
    });
  };

  if (!project) return null;

  const submittedDate = formatSubmittedDate(latestSubmission?.createdAt ?? project.createdAt);
  const projectDescription =
    stripHtml(project.project?.description || '').trim() || 'No description available.';
  const goals = getSortedProjectGoals(project.project?.goals);
  const { ratings, goalNotes } = getInitialReviewState(latestSubmission?.grades);

  return (
    <div className="relative">
      <div
        className={`flex flex-col gap-4 p-4 bg-(--neutral-white-NW1000)`}
        aria-disabled={isReviewDisabled}
      >
        <ProjectDrawerSection title="Summary">
          <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-white-NW500)! whitespace-normal wrap-break-word">
            {projectDescription}
          </p>

          <ProjectDrawerCourseDetails
            courses={project.project?.courses}
            onViewCourse={onOpenCourseDetails}
          />

          <div className="grid grid-cols-2 gap-2 pt-2">
            <div className="rounded-[10px] bg-(--neutral-black-NB100) px-3 py-3">
              <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-white-NW600)!">
                Submitted
              </p>
              <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-black-NB900)!">
                {submittedDate}
              </p>
            </div>

            <div className="rounded-[10px] bg-(--neutral-black-NB100) px-3 py-3">
              <p className="m-0! [font-size:var(--fs-l5)]! text-(--neutral-white-NW600)!">Files</p>
              <p className="m-0! [font-size:var(--fs-p5)]! text-(--neutral-black-NB900)!">
                {latestSubmission?.file ? '1 file' : 'No files'}
              </p>
            </div>
          </div>
        </ProjectDrawerSection>

        <ProjectFile file={latestSubmission?.file} onOpenFilePreview={onOpenFilePreview} />

        <ReviewForm
          goals={goals}
          initialRatings={ratings}
          initialGoalNotes={goalNotes}
          initialGeneralNote={latestSubmission?.note ?? ''}
          disabled={isReviewDisabled}
          onSubmit={onSubmit}
        />
      </div>
    </div>
  );
};
